<template>
    <div class="modal-card link-requests-card">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">
                Solicitudes de empresas pendientes
            </h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>
        <section class="modal-card-body">
            <div v-if="loading" class="has-text-centered">Cargando…</div>
            <div v-else-if="!requests.length" class="has-text-centered empty-text">
                No hay solicitudes pendientes.
            </div>
            <div v-for="request in requests" :key="request.id" class="request-card">
                <div class="request-head">
                    <div>
                        <div class="request-name">{{ request.requested_name }}</div>
                        <div class="request-meta">
                            {{ request.requested_rut }} · {{ request.user_email }}
                            <span v-if="request.role"> · {{ request.role }}</span>
                        </div>
                    </div>
                </div>
                <div class="evidence-row">
                    <span v-if="request.company_has_members" class="evidence is-warning">
                        ⚠ Ya tiene usuarios — verificá antes de aprobar
                    </span>
                    <span v-else-if="request.company_exists" class="evidence is-note">
                        Existe sin usuarios · quedará como primer usuario
                    </span>
                    <span v-else class="evidence is-new">
                        Empresa nueva · se creará al aprobar
                    </span>
                </div>
                <b-input v-model="request.rejectReason" placeholder="Motivo (si rechazás)"
                    size="is-small" class="reject-reason" />
                <div class="request-actions">
                    <button class="button is-small is-danger is-light" :disabled="busyId === request.id"
                        @click="reject(request)">
                        Rechazar
                    </button>
                    <button class="button is-small is-primary" :disabled="busyId === request.id"
                        @click="approve(request)">
                        Aprobar
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import {
    linkRequests,
    approveLinkRequest,
    rejectLinkRequest,
} from "@/api/carteraAnalista";
import { notificate, consoleHandlerError } from "@/utils/message";

export default {
    name: "LinkRequestsQueue",
    data() {
        return {
            requests: [],
            loading: false,
            busyId: null,
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const { data } = await linkRequests();
                // rejectReason inicializado upfront (v-model reactivo en Vue 2).
                this.requests = data.requests.map(
                    (request) => ({ ...request, rejectReason: "" })
                );
            } catch (error) {
                consoleHandlerError({ error });
            } finally {
                this.loading = false;
            }
        },
        async approve(request) {
            this.busyId = request.id;
            try {
                await approveLinkRequest(request.id);
                // Confirmación transitoria: auto-cierre a los 5s (default del repo).
                notificate("Solicitud aprobada.", "is-success is-light", "is-top-right", false, 5000);
                await this.load();
            } catch (error) {
                consoleHandlerError({ error });
            } finally {
                this.busyId = null;
            }
        },
        async reject(request) {
            this.busyId = request.id;
            try {
                await rejectLinkRequest(request.id, request.rejectReason || "");
                // Confirmación transitoria: auto-cierre a los 5s (default del repo).
                notificate("Solicitud rechazada.", "is-warning is-light", "is-top-right", false, 5000);
                await this.load();
            } catch (error) {
                consoleHandlerError({ error });
            } finally {
                this.busyId = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.link-requests-card {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
    width: clamp(320px, 90vw, 640px);
}

::v-deep .modal-card-title {
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--ap-ink);
}

.empty-text {
    color: var(--ap-muted);
    padding: 32px 0;
}

.request-card {
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-sm);
    padding: 16px;
    margin-bottom: 12px;
    background-color: var(--ap-paper);
    box-shadow: var(--ap-shadow-card);
}

.request-name {
    font-weight: 600;
    color: var(--ap-ink);
}

.request-meta {
    font-size: 12px;
    color: var(--ap-slate);
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
}

/* Evidence pack como pill: el analista decide de un vistazo si el RUT es nuevo,
   reclamable, o ya reclamado (rojo = requiere criterio, decisión §7.1.1). */
.evidence-row {
    margin: 10px 0;
}

.evidence {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    padding: 3px 10px;
    border-radius: var(--ap-radius-pill);
    border: 1px solid transparent;
}

.evidence.is-warning {
    color: var(--ap-alert);
    background-color: var(--ap-alert-wash);
    border-color: rgba(192, 69, 59, 0.28);
}

.evidence.is-note {
    color: var(--ap-slate);
    background-color: var(--ap-mist);
    border-color: var(--ap-line);
}

.evidence.is-new {
    color: var(--ap-teal-deep);
    background-color: var(--ap-teal-wash);
    border-color: rgba(13, 125, 128, 0.28);
}

.reject-reason {
    margin-bottom: 8px;
}

.request-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    // Acciones alineadas al tema (verde aprobar, rojo rechazar) con hover sutil.
    ::v-deep .button {
        font-weight: 600;
        transition: transform 0.14s var(--ap-ease);

        &:hover {
            transform: translateY(-1px);
        }
    }

    ::v-deep .button.is-primary {
        background-color: var(--ap-ok);
        border-color: transparent;
    }
}
</style>
